import React, { useState } from "react";
import { Dropdown, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useProfiles, useSwitchProfile } from "../../hooks/api/useProfile";

// Truncate text with ellipsis
const truncateText = (text, maxLength = 20) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

export const EmployerSwitcher = () => {
  const navigate = useNavigate();
  const { data: profiles, isLoading, error } = useProfiles();
  const switchMutation = useSwitchProfile();
  const [isOpen, setIsOpen] = useState(false);

  // Find active profile from API response
  const activeProfile = profiles?.find((p) => p.active) || profiles?.[0];

  // Handle profile click
  const handleProfileClick = (profile) => {
    // If already active, just close
    if (profile.active) {
      setIsOpen(false);
      return;
    }

    // If not completed, redirect to add company page with employer number
    if (profile.completed === false) {
      navigate(`/dashboard/add-company?employerNumber=${encodeURIComponent(profile.employerNumber)}`);
      setIsOpen(false);
      return;
    }

    // Otherwise switch normally
    switchMutation.mutate(profile.employerNumber);
  };

  // Loading state
  if (isLoading) {
    return (
      <button
        className="btn btn-white"
        style={{ display: "flex", alignItems: "center" }}
      >
        <Spin size="small" style={{ marginRight: 8 }} />
        <span>Loading...</span>
      </button>
    );
  }

  // Error state or no profiles
  if (error || !profiles || profiles.length === 0) {
    return null;
  }

  // Show dropdown for all profiles (even single) to allow adding new companies
  const dropdownContent = (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        minWidth: 280,
        maxWidth: 350,
        maxHeight: 400,
        overflow: "auto",
        padding: "8px 0",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: "#000",
            fontWeight: 700,
          }}
        >
          {profiles.length === 1 ? "Your Company" : "Switch Employer"}
        </span>
      </div>

      {/* Profile Options */}
      {profiles.map((profile) => {
        const isActive = profile.active;
        const isIncomplete = profile.completed === false;

        return (
          <div
            key={profile.employerNumber}
            onClick={() => handleProfileClick(profile)}
            style={{
              margin: "8px 16px",
              padding: "12px 16px",
              cursor: isActive ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: isActive ? "rgba(0, 150, 119, 0.08)" : "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              transition: "all 0.2s",
              opacity: switchMutation.isPending && !isActive ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isActive && !switchMutation.isPending) {
                e.currentTarget.style.background = isIncomplete 
                  ? "rgba(255, 77, 79, 0.08)" 
                  : "rgba(0, 150, 119, 0.12)";
                e.currentTarget.style.borderColor = isIncomplete ? "#ff4d4f" : "#009677";
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.borderColor = "#e0e0e0";
              }
            }}
          >
            {/* Status Dot - only for incomplete */}
            {isIncomplete && (
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#ff4d4f",
                  flexShrink: 0,
                }}
                title="Onboarding incomplete"
              />
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    fontWeight: isActive ? 600 : 500,
                    color: isIncomplete ? "#ff4d4f" : "#000",
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {profile.employerName}
                </span>
                {profile.primary && (
                  <span
                    style={{
                      fontSize: 9,
                      background: "#009677",
                      color: "#fff",
                      padding: "1px 6px",
                      borderRadius: 10,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    PRIMARY
                  </span>
                )}
              </div>
              <span style={{ fontSize: 12, color: "#888", display: "block" }}>
                {profile.employerNumber}
              </span>
              {profile.tier != null && String(profile.tier).trim() !== "" && (
                <span style={{ fontSize: 12, color: "#888", display: "block" }}>
                  Tier {String(profile.tier).replace(/^TIER[_\s-]?/i, "")}
                </span>
              )}
              {isIncomplete && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#ff4d4f",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 2,
                  }}
                >
                  <i className="bi bi-exclamation-circle" style={{ fontSize: 10 }} />
                  You haven't completed onboarding
                </span>
              )}
            </div>

            {isActive && (
              <i
                className="bi bi-check-lg"
                style={{
                  color: "#009677",
                  fontSize: 20,
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        );
      })}

      {/* Footer - Add Company */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 4,
          cursor: "pointer",
        }}
        onClick={() => {
          navigate("/dashboard/add-company");
          setIsOpen(false);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(0, 150, 119, 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <span
          style={{
            color: "#009677",
            fontWeight: 600,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <i className="bi bi-plus-lg" style={{ fontSize: 16 }} />
          Add Company
        </span>
      </div>
    </div>
  );

  return (
    <>
      {/* Full screen loading overlay when switching */}
      {switchMutation.isPending && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" style={{ marginBottom: 16 }} />
          <p style={{ color: "#009677", fontWeight: 600, fontSize: 16 }}>
            Switching company...
          </p>
        </div>
      )}
      
      <Dropdown
        open={isOpen}
        onOpenChange={setIsOpen}
        dropdownRender={() => dropdownContent}
        trigger={["click"]}
        placement="bottomLeft"
        disabled={switchMutation.isPending}
      >
        <button
          className="btn btn-white"
          style={{
            display: "flex",
            alignItems: "center",
            opacity: switchMutation.isPending ? 0.7 : 1,
          }}
        >
          <span
            className="bi bi-building"
            style={{ fontSize: "15px", color: "#009677", marginRight: 6 }}
          />
          <span
            className="ms-1"
            style={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={activeProfile?.employerName}
          >
            {truncateText(activeProfile?.employerName, 20) || "Select Employer"}
          </span>
          {switchMutation.isPending ? (
            <Spin size="small" style={{ marginLeft: 8 }} />
          ) : (
            <i
              className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
              style={{ color: "#666", fontSize: 10, marginLeft: 4 }}
            />
          )}
        </button>
      </Dropdown>
    </>
  );
};

export default EmployerSwitcher;
